// services/sharepointService.js
import { sharePointConfig } from "../authConfig";

const { siteUrl } = sharePointConfig;
const graphBase = "https://graph.microsoft.com/v1.0";

// ─── Site ID (cached per session) ───────────────────────────────────────────
let _cachedSiteId = null;

export async function getSiteId(token) {
  if (_cachedSiteId) return _cachedSiteId;
  const hostname = new URL(siteUrl).hostname;
  const sitePath = new URL(siteUrl).pathname;
  const res = await fetch(`${graphBase}/sites/${hostname}:${sitePath}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch SharePoint site ID");
  _cachedSiteId = (await res.json()).id;
  return _cachedSiteId;
}

// ─── Paged fetch ─────────────────────────────────────────────────────────────
//
// Graph caps SharePoint list-item responses at 200 rows when `expand=fields` is
// used, whatever $top asks for, and hands back an @odata.nextLink for the rest.
// Without following that link every list in this app silently truncates once it
// passes 200 items — which is exactly the point at which historical backfill
// starts to matter. This walks the whole collection.

export const GRAPH_PAGE_SIZE = 200;

export async function fetchAllPages(token, firstUrl, { errorMessage = "Failed to fetch list items", maxPages = 200 } = {}) {
  const items = [];
  let url = firstUrl;
  let page = 0;

  while (url && page < maxPages) {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error?.message || `HTTP ${res.status}: ${errorMessage}`);
    }
    const json = await res.json();
    if (Array.isArray(json.value)) items.push(...json.value);
    url = json["@odata.nextLink"] || null;
    page++;
  }
  return items;
}

// ─── SCM Weekly Data ─────────────────────────────────────────────────────────

const DATA_LIST = sharePointConfig.listName; // "SCMWeeklyData"

export async function fetchSCMRecords(token) {
  const siteId = await getSiteId(token);
  const items = await fetchAllPages(
    token,
    `${graphBase}/sites/${siteId}/lists/${DATA_LIST}/items?expand=fields&$top=${GRAPH_PAGE_SIZE}&$orderby=fields/Week_Ending desc`,
    { errorMessage: "Failed to fetch SCM records" },
  );
  return items.map((i) => i.fields);
}

export async function createSCMRecord(token, record) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${DATA_LIST}/items`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: record }),
    },
  );
  if (!res.ok) {
    const e = await res.json();
    throw new Error(e.error?.message || "Failed to create record");
  }
  return res.json();
}

export async function updateSCMRecord(token, itemId, record) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${DATA_LIST}/items/${itemId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: record }),
    },
  );
  if (!res.ok) throw new Error("Failed to update record");
  return res.json();
}

export async function deleteSCMRecord(token, itemId) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${DATA_LIST}/items/${itemId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) throw new Error("Failed to delete record");
}

export async function batchCreateSCMRecords(token, records) {
  const results = [];
  for (const r of records) {
    try {
      results.push({ success: true, data: await createSCMRecord(token, r) });
    } catch (e) {
      results.push({ success: false, error: e.message });
    }
  }
  return results;
}

// ─── Entity Schema (SCMEntitySchema list) ───────────────────────────────────
// Each item in this list = one entity definition.
// Title column = entity key (unique identifier).

const SCHEMA_LIST = "SCMEntitySchema";

/**
 * Convert a SharePoint list item → entity object
 */
function spToEntity(fields) {
  return {
    id: fields.EntityId || fields.Title,
    key: fields.Title, // Title = entity key
    name: fields.EntityName || fields.Title,
    category: fields.Category || "General",
    type: fields.FieldType || "number",
    icon: fields.Icon || "📊",
    unit: fields.Unit || "",
    cardGradient: fields.CardGradient || "blue",
    chartType: fields.ChartType || "bar",
    showOnDashboard: fields.ShowOnDashboard !== false, // Yes/No → boolean
    showInChart: fields.ShowInChart !== false,
    active: fields.Active !== false,
    sortOrder: fields.SortOrder ?? 999,
    _spItemId: fields.id || fields.ID, // SharePoint internal item ID for updates
  };
}

/**
 * Convert an entity object → SharePoint fields payload
 */
function entityToSp(entity) {
  return {
    Title: entity.key,
    EntityId: entity.id,
    EntityName: entity.name,
    Category: entity.category,
    FieldType: entity.type,
    Icon: entity.icon,
    Unit: entity.unit || "",
    CardGradient: entity.cardGradient,
    ChartType: entity.chartType,
    ShowOnDashboard: entity.showOnDashboard,
    ShowInChart: entity.showInChart,
    Active: entity.active,
    SortOrder: entity.sortOrder ?? 999,
  };
}

/**
 * Load all entities from SharePoint — sorted by SortOrder
 */
export async function fetchEntitySchema(token) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SCHEMA_LIST}/items?expand=fields&$top=200&$orderby=fields/SortOrder asc`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error("Failed to fetch entity schema");
  const items = (await res.json()).value || [];
  return items.map((i) => spToEntity({ ...i.fields, id: i.id, ID: i.id }));
}

/**
 * Create a new entity in SharePoint
 */
export async function createEntityInSchema(token, entity) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SCHEMA_LIST}/items`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: entityToSp(entity) }),
    },
  );
  if (!res.ok) {
    const e = await res.json();
    throw new Error(e.error?.message || "Failed to create entity");
  }
  const item = await res.json();
  // Return entity with SharePoint item ID attached
  return spToEntity({ ...item.fields, id: item.id });
}

/**
 * Update an existing entity (uses SharePoint item ID stored in _spItemId)
 */
export async function updateEntityInSchema(token, entity) {
  if (!entity._spItemId)
    throw new Error(`No SharePoint item ID for entity "${entity.key}"`);
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SCHEMA_LIST}/items/${entity._spItemId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: entityToSp(entity) }),
    },
  );
  if (!res.ok) throw new Error(`Failed to update entity "${entity.key}"`);
  return res.json();
}

/**
 * Delete an entity from the schema list
 */
export async function deleteEntityFromSchema(token, entity) {
  if (!entity._spItemId)
    throw new Error(`No SharePoint item ID for entity "${entity.key}"`);
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SCHEMA_LIST}/items/${entity._spItemId}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Failed to delete entity "${entity.key}"`);
}

/**
 * Bulk seed — writes the entire DEFAULT_SCHEMA to SharePoint on first run.
 * Call this once from Entity Manager if the list is empty.
 */
export async function seedEntitySchema(token, defaultSchema) {
  const results = [];
  for (let i = 0; i < defaultSchema.length; i++) {
    try {
      const created = await createEntityInSchema(token, {
        ...defaultSchema[i],
        sortOrder: i,
      });
      results.push({ success: true, entity: created });
    } catch (e) {
      results.push({
        success: false,
        key: defaultSchema[i].key,
        error: e.message,
      });
    }
  }
  return results;
}
